import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { useSession } from '@/hooks';
import { useAppContext } from '@/hooks';
import { useBranding } from '@/hooks';
import { authErrorMessage, consumeAuthErrorFromUrl } from '@/auth/authErrors';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import styles from './AppShell.module.css';
import { Spinner } from '@/components/ui';
import {
  C1Actions,
  C1Button,
  C1Card,
  C1CardBody,
  C1CardBrand,
  C1Shell,
  C1Subtitle,
  C1Title,
} from '@/components/c1';
import { AssistFab } from '@/components/shared/AssistFab';

interface AppShellProps {
  children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
  const { t } = useTranslation();
  const { status, session, login, logout } = useSession();
  const { activeContext, availableContexts, isLoading } = useAppContext();
  const { branding } = useBranding();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [authErrorCode] = useState(() => consumeAuthErrorFromUrl());
  const authError = authErrorMessage(authErrorCode, t);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('home.goodMorning');
    if (hour < 18) return t('home.goodAfternoon');
    return t('home.goodEvening');
  };

  if (status === 'loading' || isLoading) {
    return (
      <div className={styles.shell}>
        <main className={styles.main} role="status" aria-live="polite" aria-label={t('common.loading')}>
          <Spinner label={t('common.loading')} size="lg" />
        </main>
      </div>
    );
  }

  if (status === 'unauthenticated') {
    // The gate wears the identity service's own card, so signing in does not look
    // like a handover between two different products.
    return (
      <div className={styles.gate}>
        <C1Shell>
          <C1Card>
            <C1CardBrand>
              <C1Title>{branding?.portalTitle || t('common.appName')}</C1Title>
              <C1Subtitle>{authError ?? t('auth.unauthenticated')}</C1Subtitle>
            </C1CardBrand>
            <C1CardBody>
              <svg width="100%" viewBox="0 0 680 320" className={styles.gatePadlock} role="img" xmlns="http://www.w3.org/2000/svg" aria-label="Sign in required — your session is protected">
                <title>Sign in required — your session is protected</title>
                <desc>A blue rounded padlock icon indicating that signing in is needed to access your secure CampusOne workspace</desc>
                <defs>
                  <linearGradient id="lockBody" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#378ADD" />
                    <stop offset="100%" stopColor="#185FA5" />
                  </linearGradient>
                </defs>
                <g transform="translate(340,160)">
                  <circle cx="0" cy="0" r="120" fill="#E6F1FB" />
                  <path d="M -38 -20 L -38 -48 A 38 38 0 0 1 38 -48 L 38 -20" fill="none" stroke="#185FA5" strokeWidth="12" strokeLinecap="round" />
                  <rect x="-55" y="-22" width="110" height="90" rx="14" fill="url(#lockBody)" />
                  <circle cx="0" cy="14" r="10" fill="#E6F1FB" />
                  <rect x="-5" y="18" width="10" height="22" rx="4" fill="#E6F1FB" />
                </g>
              </svg>
              <C1Actions>
                <C1Button onClick={login} aria-label={t('auth.signIn')}>{t('auth.signIn')}</C1Button>
              </C1Actions>
            </C1CardBody>
          </C1Card>
        </C1Shell>
      </div>
    );
  }

  return (
    <div className={styles.shell}>
      <Sidebar
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        onSwitchContext={() => setSidebarOpen(false)}
        activeContext={activeContext}
        availableContexts={availableContexts}
        onLogout={logout}
        profileName={session?.name}
        profileEmail={session?.email}
        profileGender={session?.gender ?? null}
        portalTitle={branding?.portalTitle ?? undefined}
        logoUrl={branding?.logoUrl ?? undefined}
      />
      <div className={styles.content}>
        <header className={styles.header}>
          <button className={styles.menuBtn} onClick={() => setSidebarOpen(true)} aria-label={t('common.menu')}>
            <Icon name="menu" size={24} aria-hidden="true" />
          </button>
          <div className={styles.headerTitle}>
            <h2>{branding?.portalTitle || t('common.appName')}</h2>
          </div>
          <div className={styles.headerRight}>
            <span className={styles.greeting} aria-live="polite">{greeting()}, {session?.name?.split(' ')[0] || ''}</span>
          </div>
        </header>
        <main className={styles.main} id="main-content" tabIndex={-1}>
          {children}
        </main>
      </div>
      <MobileNav activeContext={activeContext} />
      <AssistFab />
    </div>
  );
}
